import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Stack,
  Autocomplete,
  TextareaAutosize,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
  Psychology as AIIcon,
  Code as CodeIcon,
  Create as WriteIcon,
  Science as ResearchIcon,
  Assistant as AssistantIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function CreateAgent({ darkMode }) {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [agentData, setAgentData] = useState({
    // Basic Info
    name: '',
    description: '',
    type: 'assistant',
    category: 'general',
    tags: [],

    // Configuration
    model: 'llama4',
    temperature: 0.7,
    maxTokens: 2000,
    systemPrompt: '',

    // Capabilities
    capabilities: [],
    knowledgeBase: '',
    tools: [],

    // Settings
    visibility: 'private',
    allowPublicAccess: false,
    rateLimitEnabled: true,
    maxRequestsPerHour: 100,

    // Advanced
    customInstructions: '',
    fallbackResponses: [],
    integrations: [],
  });

  const steps = [
    'Basic Information',
    'Configuration',
    'Capabilities',
    'Settings',
    'Review & Create',
  ];

  const agentTypes = [
    {
      value: 'assistant', label: 'General Assistant', icon: <AssistantIcon />, description: 'Multi-purpose AI assistant for various tasks',
    },
    {
      value: 'research', label: 'Research Agent', icon: <ResearchIcon />, description: 'Specialized in research and data analysis',
    },
    {
      value: 'coding', label: 'Code Assistant', icon: <CodeIcon />, description: 'Programming and development helper',
    },
    {
      value: 'writing', label: 'Content Creator', icon: <WriteIcon />, description: 'Writing and content generation specialist',
    },
    {
      value: 'custom', label: 'Custom Agent', icon: <AIIcon />, description: 'Build your own specialized agent',
    },
  ];

  const availableCapabilities = [
    'Text Generation',
    'Code Generation',
    'Data Analysis',
    'Image Understanding',
    'Document Processing',
    'Web Search',
    'Math Calculations',
    'Language Translation',
    'Summarization',
    'Question Answering',
    'Creative Writing',
    'Technical Writing',
  ];

  const availableTools = [
    'Calculator',
    'Web Search',
    'Code Interpreter',
    'Image Generator',
    'File Processor',
    'Database Query',
    'API Connector',
    'Email Sender',
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleInputChange = (field, value) => {
    setAgentData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayChange = (field, value) => {
    setAgentData((prev) => ({
      ...prev,
      [field]: Array.isArray(value) ? value : [value],
    }));
  };

  const handleCreateAgent = async () => {
    try {
      // Mock creation - in real app, this would call your API
      console.log('Creating agent:', agentData);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Navigate to agent manager with success message
      navigate('/agent-manager', {
        state: {
          message: `Agent "${agentData.name}" created successfully!`,
          type: 'success',
        },
      });
    } catch (error) {
      console.error('Error creating agent:', error);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <TextField
              label="Agent Name"
              value={agentData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              fullWidth
              required
              placeholder="Enter a descriptive name for your agent"
            />

            <TextField
              label="Description"
              value={agentData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe what your agent does and how it helps users"
            />

            <FormControl fullWidth>
              <InputLabel>Agent Type</InputLabel>
              <Select
                value={agentData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                label="Agent Type"
              >
                {agentTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {type.icon}
                      <Box>
                        <Typography variant="body1">{type.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              options={['AI', 'Automation', 'Research', 'Development', 'Writing', 'Analysis', 'Support']}
              value={agentData.tags}
              onChange={(event, newValue) => handleArrayChange('tags', newValue)}
              renderTags={(value, getTagProps) => value.map((option, index) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
              ))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Add tags to help categorize your agent"
                />
              )}
            />
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Base Model</InputLabel>
              <Select
                value={agentData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                label="Base Model"
              >
                <MenuItem value="llama4">LLaMA 4 (Recommended)</MenuItem>
                <MenuItem value="llama3">LLaMA 3</MenuItem>
                <MenuItem value="gpt-4">GPT-4</MenuItem>
                <MenuItem value="claude">Claude</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography gutterBottom>
                Temperature:
                {agentData.temperature}
              </Typography>
              <Box sx={{ px: 2 }}>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={agentData.temperature}
                  onChange={(e) => handleInputChange('temperature', parseFloat(e.target.value))}
                  style={{ width: '100%' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Lower values make responses more focused, higher values more creative
              </Typography>
            </Box>

            <TextField
              label="Max Tokens"
              type="number"
              value={agentData.maxTokens}
              onChange={(e) => handleInputChange('maxTokens', parseInt(e.target.value))}
              fullWidth
              inputProps={{ min: 100, max: 4000 }}
              helperText="Maximum number of tokens in the response"
            />

            <Box>
              <Typography variant="h6" gutterBottom>System Prompt</Typography>
              <TextareaAutosize
                minRows={6}
                placeholder="Define your agent's role, personality, and instructions here..."
                value={agentData.systemPrompt}
                onChange={(e) => handleInputChange('systemPrompt', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  resize: 'vertical',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                This prompt defines how your agent behaves and responds
              </Typography>
            </Box>
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" gutterBottom>Capabilities</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the capabilities your agent should have
              </Typography>
              <Grid container spacing={1}>
                {availableCapabilities.map((capability) => (
                  <Grid item key={capability}>
                    <Chip
                      label={capability}
                      clickable
                      color={agentData.capabilities.includes(capability) ? 'primary' : 'default'}
                      onClick={() => {
                        const newCapabilities = agentData.capabilities.includes(capability)
                          ? agentData.capabilities.filter((c) => c !== capability)
                          : [...agentData.capabilities, capability];
                        handleArrayChange('capabilities', newCapabilities);
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>Tools & Integrations</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select tools your agent can use
              </Typography>
              <Grid container spacing={1}>
                {availableTools.map((tool) => (
                  <Grid item key={tool}>
                    <Chip
                      label={tool}
                      clickable
                      variant="outlined"
                      color={agentData.tools.includes(tool) ? 'primary' : 'default'}
                      onClick={() => {
                        const newTools = agentData.tools.includes(tool)
                          ? agentData.tools.filter((t) => t !== tool)
                          : [...agentData.tools, tool];
                        handleArrayChange('tools', newTools);
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <TextField
              label="Knowledge Base"
              value={agentData.knowledgeBase}
              onChange={(e) => handleInputChange('knowledgeBase', e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe any specific knowledge or data your agent should be trained on"
              helperText="This helps customize the agent's responses to your specific domain"
            />
          </Stack>
        );

      case 3:
        return (
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={agentData.visibility}
                onChange={(e) => handleInputChange('visibility', e.target.value)}
                label="Visibility"
              >
                <MenuItem value="private">Private - Only you can use this agent</MenuItem>
                <MenuItem value="team">Team - Your team members can use this agent</MenuItem>
                <MenuItem value="public">Public - Anyone can discover and use this agent</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={(
                <Switch
                  checked={agentData.allowPublicAccess}
                  onChange={(e) => handleInputChange('allowPublicAccess', e.target.checked)}
                />
              )}
              label="Allow public API access"
            />

            <FormControlLabel
              control={(
                <Switch
                  checked={agentData.rateLimitEnabled}
                  onChange={(e) => handleInputChange('rateLimitEnabled', e.target.checked)}
                />
              )}
              label="Enable rate limiting"
            />

            {agentData.rateLimitEnabled && (
              <TextField
                label="Max Requests Per Hour"
                type="number"
                value={agentData.maxRequestsPerHour}
                onChange={(e) => handleInputChange('maxRequestsPerHour', parseInt(e.target.value))}
                fullWidth
                inputProps={{ min: 1, max: 10000 }}
              />
            )}

            <TextField
              label="Custom Instructions"
              value={agentData.customInstructions}
              onChange={(e) => handleInputChange('customInstructions', e.target.value)}
              fullWidth
              multiline
              rows={4}
              placeholder="Any additional instructions or constraints for your agent"
            />
          </Stack>
        );

      case 4:
        return (
          <Stack spacing={3}>
            <Alert severity="info">
              Review your agent configuration before creating
            </Alert>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                <Typography>
                  <strong>Name:</strong>
                  {' '}
                  {agentData.name}
                </Typography>
                <Typography>
                  <strong>Type:</strong>
                  {' '}
                  {agentTypes.find((t) => t.value === agentData.type)?.label}
                </Typography>
                <Typography>
                  <strong>Description:</strong>
                  {' '}
                  {agentData.description}
                </Typography>
                <Typography>
                  <strong>Tags:</strong>
                  {' '}
                  {agentData.tags.join(', ') || 'None'}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Configuration</Typography>
                <Typography>
                  <strong>Model:</strong>
                  {' '}
                  {agentData.model}
                </Typography>
                <Typography>
                  <strong>Temperature:</strong>
                  {' '}
                  {agentData.temperature}
                </Typography>
                <Typography>
                  <strong>Max Tokens:</strong>
                  {' '}
                  {agentData.maxTokens}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Capabilities & Tools</Typography>
                <Typography>
                  <strong>Capabilities:</strong>
                  {' '}
                  {agentData.capabilities.join(', ') || 'None'}
                </Typography>
                <Typography>
                  <strong>Tools:</strong>
                  {' '}
                  {agentData.tools.join(', ') || 'None'}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Settings</Typography>
                <Typography>
                  <strong>Visibility:</strong>
                  {' '}
                  {agentData.visibility}
                </Typography>
                <Typography>
                  <strong>Rate Limiting:</strong>
                  {' '}
                  {agentData.rateLimitEnabled ? `${agentData.maxRequestsPerHour} requests/hour` : 'Disabled'}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/agent-manager')}
          sx={{ mr: 2 }}
        >
          Back to Agent Manager
        </Button>
        <Typography variant="h4" component="h1">
          Create New Agent
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ mt: 3, mb: 3 }}>
          {getStepContent(activeStep)}
        </Box>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<BackIcon />}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeStep === steps.length - 1 ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={() => { /* Preview functionality */ }}
                >
                  Preview
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleCreateAgent}
                  disabled={!agentData.name.trim()}
                >
                  Create Agent
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ForwardIcon />}
                disabled={activeStep === 0 && !agentData.name.trim()}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default CreateAgent;
